import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

import { ViewerContext } from '@vimeo/viewer';

import { vimeoHCClient } from '../api/vimeoHC';
import { useLocalStorage } from '../hooks/useLocalStorage';
import {
  HelpPanelState,
  HelpPanelContextType,
  HelpPanelPage,
  HelpCategory,
  HelpArticle,
} from '../types';

const hc_base_url: string = `https://help.vimeo.com`;
const localeMap = new Map<string, string>([['en', 'en-us']]);

const getZendeskLocale = (viewerLocale: string) => {
  return localeMap.get(viewerLocale) ?? 'en-us';
};

const initialHelpPanelState: Omit<HelpPanelState, 'locale'> = {
  isPanelOpen: false,
  currentPage: 'home',
  history: [],
  currentHCUrl: `${hc_base_url}/hc`,
  hasEnterprise: false,
  searchQuery: '',
  hc_base_url: hc_base_url,
  data: {},
};

export const HelpPanelContext = createContext<HelpPanelContextType | undefined>(undefined);

export const HelpPanelProvider = ({ children }: { children: ReactNode }) => {
  const viewer = useContext(ViewerContext);
  const viewerLocale = viewer?.locale ?? 'en';
  const [locale, setLocale] = useState(getZendeskLocale(viewerLocale));

  const [state, setState] = useLocalStorage<Omit<HelpPanelState, 'locale'>>(
    'vimeo-help-panel',
    initialHelpPanelState,
    { storageKeys: ['currentPage', 'history'] },
  );
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const newZendeskLocale = getZendeskLocale(viewerLocale);
    if (locale !== newZendeskLocale) {
      setLocale(newZendeskLocale);
    }
  }, [viewerLocale, locale]);

  const openPanel = (hasEnterprise?: boolean) => {
    setState((prevState: Omit<HelpPanelState, 'locale'>) => ({
      ...prevState,
      isPanelOpen: true,
      hasEnterprise: hasEnterprise || false,
      currentPage: 'home',
      history: [],
      currentHCUrl: `${hc_base_url}/hc/${locale}`,
      data: {},
    }));
  };

  const closePanel = () => {
    setState((prevState) => ({ ...prevState, isPanelOpen: false }));
  };

  const goBack = () => {
    setState((prevState) => {
      const newHistory = [...prevState.history];
      const previousState = newHistory.pop();
      if (previousState) {
        return {
          ...prevState,
          history: newHistory,
          currentPage: previousState.page,
          currentHCUrl: previousState.hcUrl,
        };
      }
      return prevState;
    });
  };

  const goToPage = (page: HelpPanelPage, data?: any, hcUrl?: string) => {
    setState((prevState) => {
      // If navigating to the same page, just update data without pushing to history
      if (prevState.currentPage === page) {
        return {
          ...prevState,
          data: { ...prevState.data, [page]: data },
          currentHCUrl: hcUrl || prevState.currentHCUrl,
        };
      }

      // Otherwise, push current page to history and navigate to new page
      return {
        ...prevState,
        currentPage: page,
        history: [
          ...prevState.history,
          { page: prevState.currentPage, hcUrl: prevState.currentHCUrl },
        ],
        data: { ...prevState.data, [page]: data },
        currentHCUrl: hcUrl || prevState.currentHCUrl,
      };
    });
  };

  const goToSection = (category: HelpCategory) => {
    goToPage('section', category, category.url);
  };

  const goToArticleList = (section: HelpCategory) => {
    goToPage('article_list', section, section.url);
  };

  const goToArticle = (article: HelpArticle) => {
    goToPage('article', article, article.url);
  };

  const handleSearch = async (query: string) => {
    setState((prevState) => ({ ...prevState, searchQuery: query }));
    setIsSearching(true);
    try {
      const searchResults = await vimeoHCClient.searchArticles(hc_base_url, locale, query);
      goToPage('search', searchResults);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearching(false);
    }
  };

  const value = {
    ...state,
    hc_base_url,
    locale,
    isSearching,
    openPanel,
    closePanel,
    goBack,
    goToPage,
    goToSection,
    goToArticleList,
    goToArticle,
    handleSearch,
  };

  return <HelpPanelContext.Provider value={value}>{children}</HelpPanelContext.Provider>;
};
